import { CartesianGrid, Cell, Pie, PieChart } from "recharts";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
// import { TrendingUp } from "lucide-react";
import { CategoryStats } from "@/types/dashboard.types";
import { format } from "date-fns";

// Update the interface to match the new data structure

// Update component props to accept data
interface EmailCategoryChartProps {
  className?: string;
  data: CategoryStats[];
  isLoading: boolean;
  colors: {
    Primary: string;
    Promotions: string;
    Updates: string;
    Spam: string;
  };
}

const chartConfig = {
  primary: {
    label: "Primary",
    color: "hsl(142, 76%, 36%)", // Green
  },
  promotions: {
    label: "Promotions",
    color: "hsl(221, 83%, 53%)", // Blue
  },
  updates: {
    label: "Updates",
    color: "hsl(48, 96%, 53%)", // Yellow
  },
  spam: {
    label: "Spam",
    color: "hsl(0, 84%, 60%)", // Red
  },
  yahoo_inbox: {
    label: "Inbox (Yahoo)",
    color: "hsl(270, 75%, 50%)",
  },
  yahoo_spam: {
    label: "Spam (Yahoo)",
    color: "hsl(24, 85%, 55%)",
  },
  zoho_inbox: {
    label: "Inbox (Zoho)",
    color: "hsl(180, 70%, 50%)", // Teal
  },
  zoho_spam: {
    label: "Spam (Zoho)",
    color: "hsl(15, 85%, 55%)", // Orange-red
  },
  others: {
    label: "Others",
    color: "hsl(280, 70%, 50%)", // Purple
  },
  junk: {
    label: "Junk",
    color: "hsl(0, 70%, 50%)", // Dark Red
  },
  focused: {
    label: "Focused",
    color: "hsl(200, 70%, 50%)", // Light Blue
  },
} satisfies ChartConfig;

export function EmailCategoryChart({
  className,
  data,
  isLoading,
  colors,
}: EmailCategoryChartProps) {
  // Transform data for the grouped bar chart
  const transformedData = data.map((item) => ({
    date: item.date,
    primary: item.primary || 0,
    promotions: item.promotions || 0,
    updates: item.updates || 0,
    spam: item.spam || 0,
    yahoo_inbox: item.yahoo_inbox || 0,
    yahoo_spam: item.yahoo_spam || 0,
    zoho_inbox: item.zoho_inbox || 0,
    zoho_spam: item.zoho_spam || 0,
    others: item.others || 0,
    junk: item.junk || 0,
    focused: item.focused || 0,
  }));

  console.log(transformedData, 'transformedData');

  // Sort by date
  const chartData = transformedData.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const legendItems = [
    { label: "primary", color: "hsl(142, 76%, 36%)" }, // Green
    { label: "promotions", color: "hsl(221, 83%, 53%)" }, // Blue
    { label: "updates", color: "hsl(48, 96%, 53%)" }, // Yellow
    { label: "spam", color: "hsl(0, 84%, 60%)" }, // Red
    { label: "Inbox (Yahoo)", color: "hsl(270, 75%, 50%)" },
    { label: "Spam (Yahoo)", color: "hsl(24, 85%, 55%)" },
    { label: "Inbox (Zoho)", color: "hsl(180, 70%, 50%)" },
    { label: "Spam (Zoho)", color: "hsl(15, 85%, 55%)" },
    { label: "others", color: "hsl(280, 70%, 50%)" }, // Purple
    { label: "junk", color: "hsl(0, 70%, 50%)" }, // Dark Red
    { label: "focused", color: "hsl(200, 70%, 50%)" }, // Light Blue
  ];

  return (
    <Card
      className={cn("flex flex-col rounded-lg shadow-none h-full", className)}
    >
      <CardHeader className="p-4 pb-0">
        <CardTitle>Daily Email Distribution</CardTitle>
        <CardDescription>
          Daily distribution of warmup emails by category
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0 p-4">
        {isLoading ? (
          <Skeleton className="w-full h-[350px]" />
        ) : (
          <div className="h-[350px] mt-4">
            <ChartContainer config={chartConfig}>
              <BarChart accessibilityLayer data={chartData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => format(new Date(value), "MMM dd")}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: "Number of Emails",
                    angle: -90,
                    position: "left",
                    offset: -15,
                    fontSize: 12,
                    // style: { fill: "hsl(var(--muted-foreground))" },
                  }}
                  domain={[0, "auto"]}
                  allowDecimals={false}
                  tickFormatter={(value) => value.toString()}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="dashed" />}
                />
                <Bar dataKey="primary" fill="var(--color-primary)" radius={4} />
                <Bar
                  dataKey="promotions"
                  fill="var(--color-promotions)"
                  radius={4}
                />
                <Bar dataKey="updates" fill="var(--color-updates)" radius={4} />
                <Bar dataKey="spam" fill="var(--color-spam)" radius={4} />
                <Bar dataKey="yahoo_inbox" fill="var(--color-yahoo_inbox)" radius={4} />
                <Bar dataKey="yahoo_spam" fill="var(--color-yahoo_spam)" radius={4} />
                <Bar dataKey="zoho_inbox" fill="var(--color-zoho_inbox)" radius={4} />
                <Bar dataKey="zoho_spam" fill="var(--color-zoho_spam)" radius={4} />
                <Bar dataKey="others" fill="var(--color-others)" radius={4} />
                <Bar dataKey="junk" fill="var(--color-junk)" radius={4} />
                <Bar dataKey="focused" fill="var(--color-focused)" radius={4} />
              </BarChart>
            </ChartContainer>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex-col justify-center gap-2 text-sm p-4 pt-0">
        <div className="flex items-center justify-center gap-4 flex-wrap ">
          {legendItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="size-2 rounded-[2px]"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-foreground capitalize">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </CardFooter>
    </Card>
  );
}

{
  /* <ResponsiveContainer width="100%" height="100%">
<BarChart
  data={chartData}
  margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
  barGap={0}
  barCategoryGap={8}
>
  <XAxis
    dataKey="date"
    tickLine={true}
    axisLine={false}
    tick={{ fontSize: 12 }}
    tickFormatter={(value) => format(new Date(value), "MMM dd")}
    label={{
      value: "Date",
      position: "bottom",
      offset: 0,
      fontSize: 12,
      // style: { fill: "hsl(var(--muted-foreground))" },
    }}
  />
  <YAxis
    tickLine={false}
    axisLine={false}
    tick={{ fontSize: 12 }}
    label={{
      value: "Number of Emails",
      angle: -90,
      position: "left",
      offset: 0,
      fontSize: 12,
      // style: { fill: "hsl(var(--muted-foreground))" },
    }}
    domain={[0, "auto"]}
    allowDecimals={false}
    tickFormatter={(value) => value.toString()}
  />
  <Tooltip
    content={({ active, payload, label }) => {
      if (active && payload?.length) {
        // Calculate total for this date
        const total = payload.reduce(
          (sum, entry) => sum + (entry.value as number),
          0
        );

        return (
          <div className="rounded-lg border bg-background p-2 shadow-sm">
            <div className="text-[0.70rem] font-medium mb-2">
              {label}
            </div>
            <div className="space-y-1">
              {payload.map((entry, index) => {
                const percentage = (
                  ((entry.value as number) / total) *
                  100
                ).toFixed(1);
                return (
                  <div
                    key={index}
                    className="flex justify-between gap-8"
                  >
                    <span className="text-[0.70rem] text-muted-foreground">
                      {
                        chartConfig[
                          entry.name as keyof typeof chartConfig
                        ].label
                      }
                    </span>
                    <span
                      className="text-sm font-medium"
                      style={{ color: entry.color }}
                    >
                      {entry.value} ({percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      return null;
    }}
  />
  {Object.keys(chartConfig).map((category) => (
    <Bar
      key={category}
      dataKey={category}
      fill={
        chartConfig[category as keyof typeof chartConfig].color
      }
      radius={[4, 4, 0, 0]}
    />
  ))}
</BarChart>
</ResponsiveContainer> */
}
